import { Box } from '@mui/material'
import { SxProps, Theme } from '@mui/system'
import { CriticalSectionsPanel } from './CriticalSectionsPanel'
import { ProcessPerformancePanel } from './ProcessPerformancePanel'
import { useProcessStatus, getScoreColor } from '../hooks/useProcessStatus'
import { QueryMode, ProcessStatusQueryParams } from '../types'
import { IconName } from '../../../../../../../utils/icons'

interface ProcessStatusPanelProps {
  queryParams: ProcessStatusQueryParams
  queryMode: QueryMode
  scoreMap: Map<string, number>
  enabled: boolean
  onRangeApplied?: (range: [number, number]) => void
  styles: {
    processStatusLayout: SxProps<Theme>
    processPanel: SxProps<Theme>
    processPanelHeader: SxProps<Theme>
    processPanelTitle: SxProps<Theme>
    processPanelHeaderRight: SxProps<Theme>
    sliderContainer: SxProps<Theme>
    applyButton: SxProps<Theme>
    searchField: SxProps<Theme>
    questionnaireHeader: SxProps<Theme>
    questionnaireHeaderText: SxProps<Theme>
    questionnaireHeaderArrow: SxProps<Theme>
    sectionBarBlue: SxProps<Theme>
    sectionBarBlueText: SxProps<Theme>
    questionBarGrayContainer: SxProps<Theme>
    questionBarGrayLabel: SxProps<Theme>
    questionBarGrayTrack: SxProps<Theme>
    questionBarGrayFill: SxProps<Theme>
    questionBarGrayValue: SxProps<Theme>
    rightQuestionnaireRow: SxProps<Theme>
    rightQuestionnaireName: SxProps<Theme>
    rightQuestionnaireBadge: SxProps<Theme>
    rightQuestionnaireArrow: SxProps<Theme>
    rightSectionRow: SxProps<Theme>
    rightSectionLeftColumn: SxProps<Theme>
    rightSectionRightColumn: SxProps<Theme>
    rightSectionIndicator: SxProps<Theme>
    rightSectionName: SxProps<Theme>
    rightSectionTrack: SxProps<Theme>
    rightSectionFill: SxProps<Theme>
    rightSectionScore: SxProps<Theme>
    toggleButtonGroup: SxProps<Theme>
    toggleButton: SxProps<Theme>
    toggleButtonActive: SxProps<Theme>
    processDivider: SxProps<Theme>
    paginationContainer: SxProps<Theme>
    paginationButton: SxProps<Theme>
    paginationPage: SxProps<Theme>
    paginationPageActive: SxProps<Theme>
    paginationDisabled: SxProps<Theme>
  }
  icons: (name: IconName) => React.ReactNode
  t: (key: string, options?: Record<string, unknown>) => string
}

const ProcessStatusPanel = ({ queryParams, queryMode, scoreMap, enabled, onRangeApplied, styles, icons, t }: ProcessStatusPanelProps) => {
  const {
    searchLeft,
    setSearchLeft,
    searchGlobal,
    setSearchGlobal,
    sortOrder,
    setSortOrder,
    rangeValue,
    setRangeValue,
    appliedRangeValue,
    applyRange,
    isRangeDirty,
    expandedLeft,
    expandedRight,
    toggleLeft,
    toggleRight,
    pageLeft,
    setPageLeft,
    pageRight,
    setPageRight,
    paginatedLeft,
    totalPagesLeft,
    paginatedRight,
    totalPagesRight,
    criticalLoading,
    processLoading,
  } = useProcessStatus({
    queryParams,
    queryMode,
    scoreMap,
    enabled,
  })

  const handleApplyRange = () => {
    applyRange()
    if (onRangeApplied) {
      onRangeApplied(rangeValue)
    }
  }

  return (
    <Box sx={styles.processStatusLayout}>
      <CriticalSectionsPanel
        data={paginatedLeft}
        searchValue={searchLeft}
        onSearchChange={setSearchLeft}
        rangeValue={rangeValue}
        onRangeChange={setRangeValue}
        onApplyRange={handleApplyRange}
        isRangeDirty={isRangeDirty}
        expandedIds={expandedLeft}
        onToggle={toggleLeft}
        page={pageLeft}
        totalPages={totalPagesLeft}
        onPageChange={setPageLeft}
        loading={criticalLoading}
        styles={{
          processPanel: styles.processPanel,
          processPanelHeader: styles.processPanelHeader,
          processPanelTitle: styles.processPanelTitle,
          processPanelHeaderRight: styles.processPanelHeaderRight,
          sliderContainer: styles.sliderContainer,
          applyButton: styles.applyButton,
          searchField: styles.searchField,
          questionnaireHeader: styles.questionnaireHeader,
          questionnaireHeaderText: styles.questionnaireHeaderText,
          questionnaireHeaderArrow: styles.questionnaireHeaderArrow,
          sectionBarBlue: styles.sectionBarBlue,
          sectionBarBlueText: styles.sectionBarBlueText,
          questionBarGrayContainer: styles.questionBarGrayContainer,
          questionBarGrayLabel: styles.questionBarGrayLabel,
          questionBarGrayTrack: styles.questionBarGrayTrack,
          questionBarGrayFill: styles.questionBarGrayFill,
          questionBarGrayValue: styles.questionBarGrayValue,
          paginationContainer: styles.paginationContainer,
          paginationButton: styles.paginationButton,
          paginationPage: styles.paginationPage,
          paginationPageActive: styles.paginationPageActive,
          paginationDisabled: styles.paginationDisabled,
        }}
        getScoreColor={getScoreColor}
        icons={icons}
        t={t}
      />
      <ProcessPerformancePanel
        questionnaires={paginatedRight}
        searchValue={searchGlobal}
        onSearchChange={setSearchGlobal}
        sortOrder={sortOrder}
        onSortOrderChange={setSortOrder}
        expandedIds={expandedRight}
        onToggleQuestionnaire={toggleRight}
        pageRight={pageRight}
        totalPagesRight={totalPagesRight}
        onPageChangeRight={setPageRight}
        loading={processLoading}
        styles={{
          processPanel: styles.processPanel,
          processPanelHeader: styles.processPanelHeader,
          processPanelTitle: styles.processPanelTitle,
          searchField: styles.searchField,
          rightQuestionnaireRow: styles.rightQuestionnaireRow,
          rightQuestionnaireName: styles.rightQuestionnaireName,
          rightQuestionnaireBadge: styles.rightQuestionnaireBadge,
          rightQuestionnaireArrow: styles.rightQuestionnaireArrow,
          rightSectionRow: styles.rightSectionRow,
          rightSectionLeftColumn: styles.rightSectionLeftColumn,
          rightSectionRightColumn: styles.rightSectionRightColumn,
          rightSectionIndicator: styles.rightSectionIndicator,
          rightSectionName: styles.rightSectionName,
          rightSectionTrack: styles.rightSectionTrack,
          rightSectionFill: styles.rightSectionFill,
          rightSectionScore: styles.rightSectionScore,
          toggleButtonGroup: styles.toggleButtonGroup,
          toggleButton: styles.toggleButton,
          toggleButtonActive: styles.toggleButtonActive,
          processDivider: styles.processDivider,
          paginationContainer: styles.paginationContainer,
          paginationButton: styles.paginationButton,
          paginationPage: styles.paginationPage,
          paginationPageActive: styles.paginationPageActive,
          paginationDisabled: styles.paginationDisabled,
        }}
        getScoreColor={getScoreColor}
        icons={icons}
        t={t}
      />
    </Box>
  )
}

export { ProcessStatusPanel }
